import React, { FC, FormEvent, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { Input } from '@at/design-system-elements'
import { StyledPostcodeButton, StyledPostcodeDiv } from './styled-filters-postcode-input.styles'
import { FilterName } from '../../__generated__/fed-graphql-types'
import { DeviceLocationStatus, Error, UserLocationContext } from '../../contexts/user-location-context'
import { IconGeolocation } from '@at/design-system-icons'
import { getStoredPostcode } from '../../utils/search/local-search.util'

const className = 'search-filters-input'

const USER_LOCATION_ERROR_MESSAGE = 'Please enter a valid postcode'

// TODO: Sort out label/title thing for input + forms
interface SearchFiltersPostCodeInputProp {
    disableUpdateOnblur?: boolean
    dataTestId?: string
    disabled?: boolean
    placeholder?: string
    hideLabel?: boolean
    title?: string
    onChange?: (postcode: string) => void
}

export const SearchFiltersPostCodeInput: FC<SearchFiltersPostCodeInputProp> = ({
    disableUpdateOnblur = false,
    onChange = () => {},
    dataTestId = 'input-postcode',
    disabled = false,
    title = 'Postcode',
    hideLabel = false,
}: SearchFiltersPostCodeInputProp) => {
    const {
        postcode: originalPostcode,
        updatePostcode: updatePostcodeContext,
        latLong,
        requestDeviceLocation,
        deviceLocationStatus,
        error,
    } = useContext(UserLocationContext)
    const hasLatLong = latLong !== undefined
    const hasError = error !== Error.NONE
    const loadingLocation = deviceLocationStatus === DeviceLocationStatus.REQUESTED
    const postcodeInput = useRef<HTMLInputElement>(null)
    const [postcode, setPostcode] = useState(hasLatLong ? '' : originalPostcode)
    const currentPlaceHolder = hasLatLong ? 'Current location' : 'Postcode'

    const updatePostcode = useCallback(
        (value: string | undefined) => {
            const trimmedPostcode = value?.trim()
            const usingDeviceLocationAndUpdatedPostcodeIsNonEmpty = hasLatLong && !!trimmedPostcode?.length
            const usingPostcodeAndUpdatedPostcodeIsDifferentFromOriginal =
                !hasLatLong && trimmedPostcode !== originalPostcode
            if (
                usingPostcodeAndUpdatedPostcodeIsDifferentFromOriginal ||
                usingDeviceLocationAndUpdatedPostcodeIsNonEmpty
            ) {
                updatePostcodeContext(trimmedPostcode)
                setPostcode(trimmedPostcode)
            }
        },
        [originalPostcode, hasLatLong, updatePostcodeContext]
    )

    useEffect(() => {
        if (!disabled) postcodeInput.current?.focus()
    }, [disabled])

    useEffect(() => {
        setPostcode(originalPostcode)
    }, [originalPostcode])

    const isDisabled = disabled || loadingLocation

    return (
        <StyledPostcodeDiv data-testid="search-filter-postcode">
            <Input
                ref={postcodeInput}
                fieldType="text"
                className={className}
                id={FilterName.postcode}
                name={FilterName.postcode}
                label={title}
                disabled={isDisabled}
                value={postcode ?? ''}
                error={hasError && !isDisabled && originalPostcode === postcode}
                errorText={hasError ? USER_LOCATION_ERROR_MESSAGE : undefined}
                hideLabel={hideLabel}
                data-testid={dataTestId}
                autoFocus={hasError}
                placeholder={currentPlaceHolder}
                onBlur={disableUpdateOnblur ? undefined : () => updatePostcode(postcode)}
                onChange={(e: FormEvent<HTMLInputElement>) => {
                    const postcodeInput = (e?.currentTarget as HTMLInputElement)?.value
                    setPostcode(postcodeInput)
                    onChange(postcodeInput)
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void => {
                    if (event.key === 'Enter') {
                        event.preventDefault()
                        event.stopPropagation()
                        updatePostcode(postcode)
                    }
                }}
                spellCheck={false}
            />
            <StyledPostcodeButton
                data-testid="use-device-location-button"
                aria-label="Use your location"
                disabled={disabled || loadingLocation}
                onClick={() => {
                    if (hasLatLong) {
                        updatePostcodeContext(getStoredPostcode())
                    } else {
                        requestDeviceLocation()
                    }
                }}
            >
                <IconGeolocation title="Your postcode" />
            </StyledPostcodeButton>
        </StyledPostcodeDiv>
    )
}
